import {
  CALIBRATION_MAX,
  CALIBRATION_MIN,
  DEFAULT_HISTORY_ACTIVE_SAMPLE_SECONDS,
  DEFAULT_HISTORY_IDLE_SAMPLE_SECONDS,
  HISTORY_SAMPLE_MAX,
  HISTORY_SAMPLE_MIN,
  SENSOR_LOCATION_MAX,
  SENSOR_NAME_MAX,
} from '@jums-dashboard/shared';
import { useId, useState } from 'react';

import Badge from '@/components/Badge/Badge';
import Button from '@/components/Button/Button';
import ConfirmModal from '@/components/ConfirmModal/ConfirmModal';
import Switch from '@/components/Switch/Switch';

import useCurrentHome from '../hooks/useCurrentHome';
import useDeleteSensor from '../hooks/useDeleteSensor';
import useSensors from '../hooks/useSensors';
import useSetSensorEnabled from '../hooks/useSetSensorEnabled';
import useUpdateSensor from '../hooks/useUpdateSensor';
import { formatTimestamp } from '../lib/format';
import { computeFreshness, type Freshness } from '../lib/freshness';
import type { Sensor } from '../types/sensor';
import AsyncSection from './AsyncSection';
import FieldMessage from './FieldMessage';

function fmtNum(value: number | null | undefined, suffix: string, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)} ${suffix}`;
}

function fmtBattery(mv: number | null | undefined): string {
  if (mv === null || mv === undefined || !Number.isFinite(mv)) return '—';
  return `${(mv / 1000).toFixed(2)} V`;
}

const FRESHNESS_TONE: Record<Freshness, 'success' | 'warning' | 'neutral'> = {
  fresh: 'success',
  stale: 'warning',
  unknown: 'neutral',
};

const FRESHNESS_LABEL: Record<Freshness, string> = {
  fresh: 'fresh',
  stale: 'stale',
  unknown: 'no data',
};

export interface SensorsSectionProps {
  onGoToUnknown?: () => void;
}

export default function SensorsSection({ onGoToUnknown }: SensorsSectionProps) {
  const homeQuery = useCurrentHome();
  const homeId = homeQuery.data?.id;
  const list = useSensors(homeId);

  const loading = homeQuery.isPending || list.isPending;
  const error = homeQuery.isError || !homeId || list.isError;
  const errorMessage = homeQuery.isError || !homeId ? 'Failed to load home.' : 'Failed to load sensors.';
  const retry = () => {
    if (homeQuery.isError) void homeQuery.refetch();
    if (list.isError) void list.refetch();
  };

  const items = list.data ?? [];

  return (
    <AsyncSection loading={loading} error={error} errorMessage={errorMessage} onRetry={retry}>
      {homeId && (
        <div className="space-y-3">
          <p className="text-sm text-(--text-secondary)">
            Configured sensors and their latest readings. Add new sensors from <em>Unknown sensors</em>.
          </p>
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-(--border-default) bg-(--bg-surface) px-4 py-6 text-center text-sm text-(--text-secondary)">
              <span>No sensors yet. Promote a physical sensor to start tracking it.</span>
              {onGoToUnknown && (
                <Button variant="primary" size="sm" onClick={onGoToUnknown}>
                  Go to Unknown sensors
                </Button>
              )}
            </div>
          ) : (
            <ul className="space-y-2">
              {items.map((sensor) => (
                <SensorRow key={sensor.id} sensor={sensor} homeId={homeId} />
              ))}
            </ul>
          )}
        </div>
      )}
    </AsyncSection>
  );
}

interface RowProps {
  sensor: Sensor;
  homeId: string;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={`h-4 w-4 text-(--text-tertiary) transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function SensorRow({ sensor, homeId }: RowProps) {
  const [expanded, setExpanded] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const toggle = useSetSensorEnabled();
  const remove = useDeleteSensor();
  const reactId = useId();
  const panelId = `sensor-panel-${reactId}`;

  const state = sensor.currentState;
  const freshness = computeFreshness(state, sensor.config.staleAfterSeconds);
  const physicalId = sensor.physicalIds[0] ?? '—';

  const handleConfirmDelete = () => {
    if (remove.isPending) return;
    remove.mutate(
      { homeId, sensorId: sensor.id },
      {
        onSuccess: () => setConfirmingDelete(false),
        onError: () => setConfirmingDelete(false),
      },
    );
  };

  return (
    <li className="overflow-hidden rounded-lg border border-(--border-subtle) bg-(--bg-surface)">
      <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="group flex min-w-0 flex-1 items-start gap-3 text-left focus-visible:outline-none"
          aria-expanded={expanded}
          aria-controls={panelId}
        >
          <span className="mt-1 shrink-0">
            <ChevronIcon open={expanded} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-medium text-(--text-primary) group-hover:underline">{sensor.name}</span>
              <Badge tone={FRESHNESS_TONE[freshness]}>{FRESHNESS_LABEL[freshness]}</Badge>
              {!sensor.enabled && <Badge tone="neutral">disabled</Badge>}
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-(--text-secondary)">
              {sensor.location && <span>{sensor.location}</span>}
              <span>temp {fmtNum(state?.temperatureC ?? null, '°C')}</span>
              <span>humidity {fmtNum(state?.humidityPct ?? null, '%', 0)}</span>
              <span>battery {fmtBattery(state?.batteryMv ?? null)}</span>
            </span>
            <span className="mt-1 block text-xs text-(--text-tertiary)">
              {state ? `Updated ${formatTimestamp(state.observedAt)}` : 'No readings received yet.'}
            </span>
          </span>
        </button>
        <div className="shrink-0">
          <Switch
            label={sensor.enabled ? 'Enabled' : 'Disabled'}
            hideLabel
            checked={sensor.enabled}
            disabled={toggle.isPending}
            onChange={(next) => toggle.mutate({ homeId, sensorId: sensor.id, enabled: next })}
          />
        </div>
      </div>
      {expanded && (
        <div id={panelId} className="border-t border-(--border-subtle) bg-(--bg-surface-strong) px-4 py-3">
          <SensorEditForm
            sensor={sensor}
            homeId={homeId}
            physicalId={physicalId}
            onDelete={() => setConfirmingDelete(true)}
            deleting={remove.isPending}
          />
        </div>
      )}
      <ConfirmModal
        open={confirmingDelete}
        title="Delete sensor?"
        body={
          <>
            Delete sensor <strong className="text-(--text-primary)">{sensor.name}</strong>? This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!remove.isPending) setConfirmingDelete(false);
        }}
      />
    </li>
  );
}

interface EditProps {
  sensor: Sensor;
  homeId: string;
  physicalId: string;
  onDelete: () => void;
  deleting: boolean;
}

function SensorEditForm({ sensor, homeId, physicalId, onDelete, deleting }: EditProps) {
  const update = useUpdateSensor();
  const [name, setName] = useState(sensor.name);
  const [location, setLocation] = useState(sensor.location);
  const [offsetText, setOffsetText] = useState(String(sensor.config.calibrationOffsetC ?? 0));
  const [idleText, setIdleText] = useState(String(sensor.config.historyIdleSampleSeconds ?? DEFAULT_HISTORY_IDLE_SAMPLE_SECONDS));
  const [activeText, setActiveText] = useState(String(sensor.config.historyActiveSampleSeconds ?? DEFAULT_HISTORY_ACTIVE_SAMPLE_SECONDS));

  const trimmedName = name.trim();
  const trimmedLocation = location.trim();
  const parsedOffset = Number(offsetText);
  const parsedIdle = Number(idleText);
  const parsedActive = Number(activeText);
  const nameValid = trimmedName.length >= 1 && trimmedName.length <= SENSOR_NAME_MAX;
  const locationValid = trimmedLocation.length <= SENSOR_LOCATION_MAX;
  const offsetValid = Number.isFinite(parsedOffset) && parsedOffset >= CALIBRATION_MIN && parsedOffset <= CALIBRATION_MAX;
  const idleValid =
    Number.isFinite(parsedIdle) && Number.isInteger(parsedIdle) && parsedIdle >= HISTORY_SAMPLE_MIN && parsedIdle <= HISTORY_SAMPLE_MAX;
  const activeValid =
    Number.isFinite(parsedActive) &&
    Number.isInteger(parsedActive) &&
    parsedActive >= HISTORY_SAMPLE_MIN &&
    parsedActive <= HISTORY_SAMPLE_MAX;

  const nameError = !nameValid ? `Name must be 1–${SENSOR_NAME_MAX} characters.` : '';
  const locationError = !locationValid ? `Location must be at most ${SENSOR_LOCATION_MAX} characters.` : '';
  const offsetError = !offsetValid ? `Offset must be between ${CALIBRATION_MIN} and ${CALIBRATION_MAX} °C.` : '';
  const idleError = !idleValid ? `Idle interval must be a whole number between ${HISTORY_SAMPLE_MIN} and ${HISTORY_SAMPLE_MAX} s.` : '';
  const activeError = !activeValid
    ? `Active interval must be a whole number between ${HISTORY_SAMPLE_MIN} and ${HISTORY_SAMPLE_MAX} s.`
    : '';

  const currentIdle = sensor.config.historyIdleSampleSeconds ?? DEFAULT_HISTORY_IDLE_SAMPLE_SECONDS;
  const currentActive = sensor.config.historyActiveSampleSeconds ?? DEFAULT_HISTORY_ACTIVE_SAMPLE_SECONDS;
  const dirty =
    trimmedName !== sensor.name ||
    trimmedLocation !== sensor.location ||
    parsedOffset !== (sensor.config.calibrationOffsetC ?? 0) ||
    parsedIdle !== currentIdle ||
    parsedActive !== currentActive;
  const canSave = nameValid && locationValid && offsetValid && idleValid && activeValid && dirty && !update.isPending;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        update.mutate({
          homeId,
          sensorId: sensor.id,
          name: trimmedName,
          location: trimmedLocation,
          calibrationOffsetC: parsedOffset,
          historyIdleSampleSeconds: parsedIdle,
          historyActiveSampleSeconds: parsedActive,
        });
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={SENSOR_NAME_MAX}
            aria-invalid={!nameValid || undefined}
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
          <FieldMessage tone="error">{nameError}</FieldMessage>
        </label>
        <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
          Location
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={SENSOR_LOCATION_MAX}
            aria-invalid={!locationValid || undefined}
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
          <FieldMessage tone="error">{locationError}</FieldMessage>
        </label>
        <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
          Calibration offset (°C)
          <input
            type="number"
            step="0.1"
            min={CALIBRATION_MIN}
            max={CALIBRATION_MAX}
            value={offsetText}
            onChange={(e) => setOffsetText(e.target.value)}
            aria-invalid={!offsetValid || undefined}
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
          <FieldMessage tone="error">{offsetError}</FieldMessage>
        </label>
        <div className="space-y-1 text-xs font-medium text-(--text-secondary)">
          Physical id
          <code className="block w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 font-mono text-xs text-(--text-primary)">
            {physicalId}
          </code>
        </div>
        <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
          History sample interval — idle (s)
          <input
            type="number"
            step="1"
            min={HISTORY_SAMPLE_MIN}
            max={HISTORY_SAMPLE_MAX}
            value={idleText}
            onChange={(e) => setIdleText(e.target.value)}
            aria-invalid={!idleValid || undefined}
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
          {idleError ? (
            <FieldMessage tone="error">{idleError}</FieldMessage>
          ) : (
            <FieldMessage>Default {DEFAULT_HISTORY_IDLE_SAMPLE_SECONDS}s.</FieldMessage>
          )}
        </label>
        <label className="space-y-1 text-xs font-medium text-(--text-secondary)">
          History sample interval — active (s)
          <input
            type="number"
            step="1"
            min={HISTORY_SAMPLE_MIN}
            max={HISTORY_SAMPLE_MAX}
            value={activeText}
            onChange={(e) => setActiveText(e.target.value)}
            aria-invalid={!activeValid || undefined}
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm font-normal text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
          {activeError ? (
            <FieldMessage tone="error">{activeError}</FieldMessage>
          ) : (
            <FieldMessage>
              Default {DEFAULT_HISTORY_ACTIVE_SAMPLE_SECONDS}s. Used during a sauna session; idle interval applies otherwise.
            </FieldMessage>
          )}
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        <Button type="button" variant="destructive" size="sm" onClick={onDelete} loading={deleting}>
          Delete sensor
        </Button>
        <Button type="submit" variant="primary" size="sm" loading={update.isPending} disabled={!canSave}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
